
import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock,
  HeartPulse,
  Menu,
  Phone,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  X,
} from "lucide-react";

const treatments = [
  {
    id: 1,
    title: "Orthopedic Physiotherapy",
    shortTitle: "Orthopedic",
    description:
      "Evidence-based rehabilitation for musculoskeletal injuries, pain, and movement limitations.",
    services: [
      "Arthritis and joint pain",
      "Frozen shoulder",
      "Tennis and golfer's elbow",
      "Knee and back pain",
      "Sports-related injuries",
    ],
    icon: "🦴",
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=900&auto=format&fit=crop&q=80",
    gradient: "from-blue-500 to-cyan-400",
  },
  {
    id: 2,
    title: "Neurological Physiotherapy",
    shortTitle: "Neurological",
    description:
      "Personalized therapy to improve mobility, balance, coordination, and independence.",
    services: [
      "Stroke rehabilitation",
      "Parkinson's disease",
      "Spinal cord conditions",
      "Balance and coordination",
      "Neurological movement disorders",
    ],
    icon: "🧠",
    image:
      "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=900&auto=format&fit=crop&q=80",
    gradient: "from-indigo-500 to-blue-400",
  },
  {
    id: 3,
    title: "Geriatric Physiotherapy",
    shortTitle: "Geriatric",
    description:
      "Specialized rehabilitation for older adults to support strength, mobility, and quality of life.",
    services: [
      "Age-related weakness",
      "Balance and fall prevention",
      "Joint stiffness",
      "Muscle strengthening",
      "Post-operative rehabilitation",
    ],
    icon: "👴",
    image:
      "https://images.unsplash.com/photo-1576765608866-5b51046452be?w=900&auto=format&fit=crop&q=80",
    gradient: "from-teal-500 to-emerald-400",
  },
  {
    id: 4,
    title: "Pediatric Physiotherapy",
    shortTitle: "Pediatric",
    description:
      "Child-focused physiotherapy that supports movement, posture, coordination, and development.",
    services: [
      "Developmental delays",
      "Postural correction",
      "Muscle weakness",
      "Balance and coordination",
      "Pediatric movement therapy",
    ],
    icon: "🧒",
    image:
      "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=900&auto=format&fit=crop&q=80",
    gradient: "from-pink-500 to-orange-400",
  },
  {
    id: 5,
    title: "Speech & Language Therapy",
    shortTitle: "Speech Therapy",
    description:
      "Support for communication, speech, language, and swallowing-related rehabilitation needs.",
    services: [
      "Speech and language difficulties",
      "Voice-related concerns",
      "Communication support",
      "Swallowing rehabilitation",
      "Speech-related neurological conditions",
    ],
    icon: "🗣️",
    image:
      "https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=900&auto=format&fit=crop&q=80",
    gradient: "from-violet-500 to-fuchsia-400",
  },
  {
    id: 6,
    title: "Gynecological Physiotherapy",
    shortTitle: "Gynecological",
    description:
      "Specialized physiotherapy to support pelvic health, functional recovery, and physical wellbeing.",
    services: [
      "Pelvic floor rehabilitation",
      "Postpartum recovery",
      "Pelvic pain management",
      "Core strengthening",
      "Movement rehabilitation",
    ],
    icon: "🌸",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&auto=format&fit=crop&q=80",
    gradient: "from-rose-500 to-pink-400",
  },
  {
    id: 7,
    title: "Cardiopulmonary Physiotherapy",
    shortTitle: "Cardiopulmonary",
    description:
      "Physiotherapy designed to support respiratory function, endurance, and recovery.",
    services: [
      "Breathing exercises",
      "Respiratory rehabilitation",
      "COPD support",
      "Post-operative recovery",
      "Endurance training",
    ],
    icon: "🫁",
    image:
      "https://images.unsplash.com/photo-1584515933487-779824d29309?w=900&auto=format&fit=crop&q=80",
    gradient: "from-cyan-500 to-sky-400",
  },
  {
    id: 8,
    title: "Rehabilitation & Pain Management",
    shortTitle: "Pain Management",
    description:
      "Comprehensive rehabilitation techniques to improve function and manage musculoskeletal pain.",
    services: [
      "IFT and TENS",
      "Ultrasound therapy",
      "Exercise therapy",
      "Dry needling",
      "Manual therapy",
    ],
    icon: "💪",
    image:
      "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=900&auto=format&fit=crop&q=80",
    gradient: "from-amber-500 to-orange-400",
  },
  {
    id: 9,
    title: "Industrial & Ergonomic Physiotherapy",
    shortTitle: "Ergonomic",
    description:
      "Workplace-focused care to support posture, movement, and injury prevention.",
    services: [
      "Workplace posture assessment",
      "Ergonomic training",
      "Muscle strain management",
      "Postural correction",
      "Work-related injury prevention",
    ],
    icon: "🏢",
    image:
      "https://images.unsplash.com/photo-1521791055366-0d553872125f?w=900&auto=format&fit=crop&q=80",
    gradient: "from-slate-600 to-slate-400",
  },
  {
    id: 10,
    title: "Sports Physiotherapy",
    shortTitle: "Sports",
    description:
      "Performance-focused rehabilitation for athletes and active individuals.",
    services: [
      "Sports injury rehabilitation",
      "Muscle strain recovery",
      "Strength and conditioning",
      "Mobility improvement",
      "Injury prevention",
    ],
    icon: "🏃",
    image:
      "https://images.unsplash.com/photo-1538805060514-97d9cc17730c?w=900&auto=format&fit=crop&q=80",
    gradient: "from-green-500 to-lime-400",
  },
];

const specialties = [
  "Neurorehabilitation Specialists",
  "Orthopedic Specialists",
  "Medical Specialists",
  "Surgical Rehabilitation Specialists",
  "Physical Medicine Specialists",
];

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: "easeOut" },
  },
};

function Treatments() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);

  const scrollToTreatments = () => {
    document
      .getElementById("treatments")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen overflow-hidden bg-slate-50 text-slate-800">
      {/* Navigation */}
      
      {/* Hero */}
      <section className="relative isolate pt-28">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-50 via-white to-cyan-50" />
        <div className="absolute -left-32 top-10 -z-10 h-96 w-96 rounded-full bg-cyan-200/30 blur-3xl" />
        <div className="absolute -right-20 top-32 -z-10 h-96 w-96 rounded-full bg-blue-200/30 blur-3xl" />

        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-2 lg:px-8 lg:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-xs font-bold uppercase tracking-wider text-blue-600 shadow-sm">
              <Sparkles size={14} />
              Advanced Rehabilitation Care
            </div>

            <h2 className="text-4xl font-black leading-[1.12] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Restore Your
              <span className="block text-blue-600">Movement.</span>
              <span className="block">Renew Your Life.</span>
            </h2>

            <p className="mt-6 max-w-xl text-base leading-8 text-slate-600 sm:text-lg">
              Physiotherapy is not only about reducing pain. It is a modern,
              science-based approach to restoring natural body function,
              improving mobility, and helping you live better.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={scrollToTreatments}
                className="group flex items-center gap-3 rounded-full bg-blue-600 px-6 py-4 font-bold text-white shadow-xl shadow-blue-200 transition hover:bg-blue-700"
              >
                Explore Treatments
                <ArrowRight
                  size={18}
                  className="transition group-hover:translate-x-1"
                />
              </button>

              <a
                href="#contact"
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-4 font-bold text-slate-700 shadow-sm transition hover:border-blue-300 hover:text-blue-600"
              >
                <Phone size={18} />
                Contact Us
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold text-slate-600">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="text-emerald-500" size={18} />
                Personalized Care
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="text-emerald-500" size={18} />
                Professional Team
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="text-emerald-500" size={18} />
                Modern Techniques
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative mx-auto w-full max-w-xl"
          >
            <div className="absolute -inset-5 rounded-[3rem] bg-gradient-to-br from-blue-200/50 to-cyan-200/30 blur-2xl" />

            <div className="relative overflow-hidden rounded-[2.5rem] border-8 border-white shadow-2xl shadow-blue-200/60">
              <img
                src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&auto=format&fit=crop&q=85"
                alt="Professional physiotherapy treatment"
                className="h-[420px] w-full object-cover sm:h-[480px]"
              />

              <div className="absolute inset-x-4 bottom-4 rounded-3xl border border-white/40 bg-white/90 p-4 shadow-xl backdrop-blur-md">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
                    <ShieldCheck size={25} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">
                      Patient-Centered Care
                    </p>
                    <p className="text-xs text-slate-500">
                      Supporting your recovery journey
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -right-3 top-8 hidden rounded-2xl border border-white bg-white p-4 shadow-xl sm:block"
            >
              <HeartPulse className="mb-1 text-blue-600" size={25} />
              <p className="text-xs font-bold text-slate-700">
                Better Mobility
              </p>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-5 bottom-28 hidden rounded-2xl border border-white bg-white p-4 shadow-xl sm:block"
            >
              <Stethoscope className="mb-1 text-cyan-600" size={25} />
              <p className="text-xs font-bold text-slate-700">
                Expert Guidance
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section id="about" className="bg-white px-5 py-16 lg:px-8 lg:py-24">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
          className="mx-auto max-w-4xl text-center"
        >
          <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-blue-600">
            Our Approach
          </span>

          <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
            Comprehensive Physiotherapy Treatments
          </h2>

          <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-slate-600">
            Our rehabilitation services focus on restoring function,
            supporting recovery, and improving quality of life. Treatment
            plans should be selected according to individual needs and
            assessment by qualified professionals.
          </p>
        </motion.div>

        <div className="mx-auto mt-12 grid max-w-6xl gap-6 sm:grid-cols-3">
          {[
            {
              icon: <Stethoscope size={28} />,
              title: "Professional Assessment",
              text: "Understand your functional needs.",
            },
            {
              icon: <HeartPulse size={28} />,
              title: "Personalized Treatment",
              text: "Care tailored to your rehabilitation goals.",
            },
            {
              icon: <ShieldCheck size={28} />,
              title: "Recovery Support",
              text: "Guidance throughout your rehabilitation.",
            },
          ].map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.12 }}
              className="rounded-3xl border border-slate-100 bg-slate-50 p-7 text-center transition hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
                {item.icon}
              </div>
              <h3 className="mt-5 font-bold text-slate-900">{item.title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-500">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Treatments */}
      <section
        id="treatments"
        className="relative bg-gradient-to-b from-slate-50 to-blue-50/50 px-5 py-20 lg:px-8 lg:py-24"
      >
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mx-auto max-w-3xl text-center"
          >
            <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-blue-600">
              What We Offer
            </span>

            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Our Treatments
            </h2>

            <p className="mt-5 leading-8 text-slate-600">
              Explore our specialized physiotherapy services. Select a
              treatment to learn more about the rehabilitation techniques and
              areas of care.
            </p>
          </motion.div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {treatments.map((treatment, index) => (
              <motion.article
                key={treatment.id}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
                whileHover={{ y: -8 }}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-white bg-white shadow-sm transition-shadow hover:shadow-2xl hover:shadow-blue-100/80"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={treatment.image}
                    alt={treatment.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />

                  <div className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-2xl border border-white/30 bg-white/90 text-xl shadow-lg backdrop-blur-sm">
                    {treatment.icon}
                  </div>

                  <span className="absolute bottom-4 left-5 text-xs font-bold uppercase tracking-wider text-white/80">
                    Specialized Care
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-6">
                  <h3 className="text-xl font-extrabold leading-snug text-slate-900">
                    {treatment.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-500">
                    {treatment.description}
                  </p>

                  <div className="mt-5 space-y-2.5">
                    {treatment.services.slice(0, 3).map((service) => (
                      <div
                        key={service}
                        className="flex items-start gap-2 text-sm text-slate-600"
                      >
                        <CheckCircle2
                          size={16}
                          className="mt-0.5 shrink-0 text-blue-500"
                        />
                        <span>{service}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto pt-6">
                    <button
                      onClick={() => setSelectedTreatment(treatment)}
                      className="flex w-full items-center justify-between rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 transition hover:bg-blue-600 hover:text-white"
                    >
                      View Treatment Details
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Specialists */}
      <section
        id="specialties"
        className="relative overflow-hidden bg-slate-900 px-5 py-20 text-white lg:px-8 lg:py-24"
      >
        <div className="absolute -right-40 -top-40 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-cyan-600/10 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-cyan-300">
              Professional Expertise
            </span>

            <h2 className="mt-4 text-3xl font-black leading-tight sm:text-4xl">
              Specialists for Your
              <span className="block text-cyan-300">Rehabilitation Needs</span>
            </h2>

            <p className="mt-5 max-w-lg leading-8 text-slate-300">
              Our services can be coordinated with relevant healthcare
              professionals. Treatment plans depend on the patient's condition,
              assessment, and rehabilitation goals.
            </p>

            <a
              href="#contact"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-cyan-400 px-6 py-4 font-bold text-slate-950 transition hover:bg-cyan-300"
            >
              Get in Touch
              <ArrowRight size={18} />
            </a>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-md sm:p-8"
          >
            <div className="mb-6 flex items-center gap-3">
              <div className="rounded-xl bg-cyan-400/15 p-3 text-cyan-300">
                <Stethoscope size={24} />
              </div>
              <h3 className="text-xl font-bold">Areas of Specialization</h3>
            </div>

            <div className="space-y-3">
              {specialties.map((specialty, index) => (
                <motion.div
                  key={specialty}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:bg-white/10"
                >
                  <CheckCircle2 className="shrink-0 text-cyan-300" size={20} />
                  <span className="text-sm font-semibold text-slate-200">
                    {specialty}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section
        id="contact"
        className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-cyan-500 px-5 py-20 lg:px-8"
      >
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full border-[40px] border-white/10" />
        <div className="absolute -bottom-40 -left-20 h-80 w-80 rounded-full border-[50px] border-white/10" />

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="relative mx-auto max-w-4xl text-center text-white"
        >
          <span className="text-sm font-extrabold uppercase tracking-[0.2em] text-blue-100">
            Take the Next Step
          </span>

          <h2 className="mt-4 text-3xl font-black sm:text-5xl">
            Start Your Rehabilitation Journey
          </h2>

          <p className="mx-auto mt-5 max-w-2xl leading-8 text-blue-50">
            Contact our team to learn about available services and arrange an
            appointment with a qualified healthcare professional.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="tel:+8801000000000"
              className="flex items-center gap-3 rounded-full bg-white px-7 py-4 font-bold text-blue-700 shadow-xl transition hover:bg-blue-50"
            >
              <Phone size={18} />
              Call for Appointment
            </a>

            <a
              href="mailto:info@example.com"
              className="flex items-center gap-3 rounded-full border border-white/40 bg-white/10 px-7 py-4 font-bold text-white transition hover:bg-white/20"
            >
              Email Us
              <ArrowRight size={18} />
            </a>
          </div>
        </motion.div>
      </section>

     
      {/* Treatment Modal */}
      {selectedTreatment && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => setSelectedTreatment(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white shadow-2xl"
          >
            <div className="relative h-52">
              <img
                src={selectedTreatment.image}
                alt={selectedTreatment.title}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />

              <button
                onClick={() => setSelectedTreatment(null)}
                className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-slate-700 shadow-lg transition hover:bg-white"
                aria-label="Close details"
              >
                <X size={20} />
              </button>

              <h3 className="absolute bottom-5 left-6 right-6 text-2xl font-extrabold text-white">
                {selectedTreatment.title}
              </h3>
            </div>

            <div className="p-6 sm:p-8">
              <p className="leading-7 text-slate-600">
                {selectedTreatment.description}
              </p>

              <h4 className="mt-6 font-bold text-slate-900">
                Areas of Care
              </h4>

              <ul className="mt-4 space-y-3">
                {selectedTreatment.services.map((service) => (
                  <li
                    key={service}
                    className="flex items-start gap-3 text-sm text-slate-600"
                  >
                    <CheckCircle2
                      size={18}
                      className="mt-0.5 shrink-0 text-blue-600"
                    />
                    {service}
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                onClick={() => setSelectedTreatment(null)}
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-4 font-bold text-white transition hover:bg-blue-700"
              >
                Contact for Appointment
                <ArrowRight size={18} />
              </a>

              <p className="mt-4 flex items-start gap-2 text-xs leading-5 text-slate-400">
                <Clock size={14} className="mt-0.5 shrink-0" />
                Treatment suitability and session plans are determined through
                professional assessment.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default Treatments;